import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { api } from '../../lib/api';
import { useDeckStore } from '../../store/deckStore';

/** Admins: publish the open deck as an approved company template, with a name and description. */
export function PublishTemplateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const deckId = useDeckStore((s) => s.deckId);
  const deckTitle = useDeckStore((s) => s.title);
  const slideCount = useDeckStore((s) => s.slides.length);
  const syncSave = useDeckStore((s) => s.syncSave);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const publish = async () => {
    if (!deckId) return;
    setBusy(true);
    setError(null);
    try {
      // The server copies the saved deck, so save pending edits first.
      await syncSave();
      await api.createTemplate({ deckId, title: title.trim(), description: description.trim() });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not publish the template');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="publish-template-title"
      slotProps={{
        transition: {
          onEnter: () => {
            setTitle(deckTitle);
            setDescription('');
            setError(null);
            setDone(false);
          },
        },
      }}
    >
      <DialogTitle id="publish-template-title" component="h2" variant="h6">
        Publish as company template
      </DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success">
            Published. Everyone can now start a deck from “{title.trim()}” under New deck → templates. Manage templates in
            Admin settings.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Copies this deck's {slideCount} slides as it is now. Later edits to the deck don't change the template.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Template name"
              autoFocus
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 120 } }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText="When to use it, e.g. “Monthly HR update for leadership”"
              slotProps={{ htmlInput: { maxLength: 280 } }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="outlined" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={busy || !title.trim() || !deckId}
              onClick={() => void publish()}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Publish template
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
